import React, { useState } from "react";
import { Star } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { REVIEWS, REVIEWS_META } from "../mock";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Stars = ({ className = "h-4 w-4" }) => (
  <span className="inline-flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${className} text-[#FBBC05]`} fill="#FBBC05" />
    ))}
  </span>
);

const LIMIT = 480;

const ReviewCard = ({ r, i }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTrim = r.text.length > LIMIT;
  const shown = expanded || !needsTrim ? r.text : `${r.text.slice(0, LIMIT).trim()}… `;

  return (
    <div
      className="rounded-2xl border border-brand-100 bg-sand-50 p-6 shadow-sm reveal flex flex-col h-full"
      style={{ transitionDelay: `${i * 80}ms` }}
      data-testid={`review-card-${i}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
            style={{ backgroundColor: r.color }}
          >
            {r.initials}
          </span>
          <div className="text-left">
            <p className="font-semibold text-brand-900 leading-tight">{r.name}</p>
            <p className="text-xs text-brand-900/50">{r.reviewCount}</p>
          </div>
        </div>
        <GoogleG />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Stars />
        <span className="text-xs text-brand-900/50">{r.when}</span>
      </div>
      <p className="mt-3 text-sm text-brand-900/75 leading-relaxed">
        {shown}
        {needsTrim && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="font-semibold text-brand-600 hover:text-brand-800 transition-colors"
            data-testid={`review-readmore-${i}`}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        )}
      </p>
    </div>
  );
};

export default function ReviewsSection() {
  useReveal();
  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col items-center text-center reveal">
          <div className="flex items-center gap-3">
            <GoogleG className="h-8 w-8" />
            <span className="font-display text-2xl sm:text-3xl text-brand-900">{REVIEWS_META.label}</span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-4xl font-semibold text-brand-900">{REVIEWS_META.rating}</span>
            <Stars className="h-6 w-6" />
          </div>
          <p className="mt-3 text-brand-900/60">What our TMS &amp; Esketamine clients say about AwareHub</p>
        </div>

        <div className="mt-10 grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-stretch">
          {REVIEWS.slice(0, 9).map((r, i) => (
            <ReviewCard key={i} r={r} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
